import { semanticColors } from '../../config/theme';

/**
 * Shared catalog carousel tile geometry (flowers + mailed cards via
 * {@link CatalogCompactTile}). Public e-card tiles
 * ({@link TemplateSelectionTile} `publicVisual`) reuse
 * {@link CATALOG_TILE_TEXT_BAND_HEIGHT} for the bottom title/price band so
 * carousel rows share the same footer structure.
 */

/** Fixed carousel / browse tile width — matches FlowerPreviewTile, mailed-card, and gift tiles. */
export const CATALOG_TILE_WIDTH = 200;

/**
 * Public e-card carousel tile width (20% wider than {@link CATALOG_TILE_WIDTH}).
 * Used by {@link PublicEcardCatalogTile} and landing Explore carousels.
 */
export const ECARD_PUBLIC_TILE_WIDTH = CATALOG_TILE_WIDTH * 1.2;

/**
 * Fixed public e-card tile height — shorter than {@link CATALOG_TILE_HEIGHT}
 * because landscape 16:9 media does not need a tall portrait frame.
 */
export const ECARD_PUBLIC_TILE_HEIGHT = CATALOG_TILE_WIDTH * (7 / 5);

/** Landscape media surface for public e-card tiles. */
export const ECARD_PUBLIC_MEDIA_ASPECT_RATIO = '16 / 9';

/** Mantine radius token for public e-card media surfaces. */
export const ECARD_PUBLIC_MEDIA_RADIUS = 'md';

/**
 * Locked text-band height (name + price). Must be applied as height + min/max
 * with overflow hidden — flex `min-height: auto` otherwise lets two-line titles
 * grow the band and misalign carousel rows.
 */
export const CATALOG_TILE_TEXT_BAND_HEIGHT = 80;

/** Portrait product image ratio (flower-product-images rule). */
export const CATALOG_TILE_IMAGE_ASPECT = '4 / 5';

/**
 * Image-area height at {@link CATALOG_TILE_WIDTH} for a 4:5 aspect box
 * (width / height = 4/5 → height = width × 5/4).
 */
export const CATALOG_TILE_IMAGE_AREA_HEIGHT =
  (CATALOG_TILE_WIDTH * 5) / 4;

/**
 * Locked outer tile height at default width so every carousel card matches
 * regardless of title length.
 */
export const CATALOG_TILE_HEIGHT =
  CATALOG_TILE_IMAGE_AREA_HEIGHT + CATALOG_TILE_TEXT_BAND_HEIGHT;

/** Warm neutral behind artwork (event tint applies to header band / browse section). */
export const CATALOG_TILE_IMAGE_AREA_BG = semanticColors.bg.page;

export const CATALOG_TILE_IMAGE_SURFACE_RADIUS = 'md';

/**
 * How far a promo overlay tucks over the product photo (px). The rest of the
 * badge sits in the event-tinted margin above the image; wrapping headlines
 * grow that margin instead of covering more of the photo.
 */
export const CATALOG_TILE_OVERLAY_IMAGE_OVERLAP = 12;

export const CATALOG_TILE_DEFAULT_EVENT_COLOR = 'indigo';

/**
 * Mantine shade for {@link CatalogCompactTile} event-type labels above artwork.
 * Darker than the `-0` tinted shell so the occasion name stays readable without
 * competing with the card title in the text band.
 */
export const CATALOG_TILE_EVENT_TYPE_LABEL_SHADE = 7;

/** Selected picker tile outline width — matches FlowerOrderModal arrangement grid. */
export const CATALOG_PICKER_SELECTED_BORDER_WIDTH = 2;

/** Default picker tile outline width when not selected. */
export const CATALOG_PICKER_UNSELECTED_BORDER_WIDTH = 1;

/**
 * Mantine CSS variable for a strong picker-grid selection outline.
 * Shade `-5` reads clearly on white modal backgrounds (work item: mailed-card picker).
 */
export function catalogPickerSelectedBorderColor(eventColor: string | null | undefined): string {
  const color = eventColor ?? CATALOG_TILE_DEFAULT_EVENT_COLOR;
  return `var(--mantine-color-${color}-5)`;
}

/** Subtle event-tinted outline for unselected picker tiles. */
export function catalogPickerUnselectedBorderColor(eventColor: string | null | undefined): string {
  const color = eventColor ?? CATALOG_TILE_DEFAULT_EVENT_COLOR;
  return `var(--mantine-color-${color}-3)`;
}

/**
 * Event-tinted artwork shell background for carousel / shop-grid tiles.
 * Matches {@link CatalogBrowseTile} image sections and {@link CatalogTileTextBand}.
 */
export function catalogTileArtworkBackgroundColor(
  eventColor: string | null | undefined,
  options?: { allowNeutralWhenMissing?: boolean },
): string {
  if (options?.allowNeutralWhenMissing && !eventColor) {
    return CATALOG_TILE_IMAGE_AREA_BG;
  }

  const color = eventColor ?? CATALOG_TILE_DEFAULT_EVENT_COLOR;
  return `var(--mantine-color-${color}-0)`;
}

/**
 * Darker event-tint CSS color for occasion labels on catalog tiles.
 * Pairs with {@link catalogTileArtworkBackgroundColor} (`-0` shell).
 */
export function catalogTileEventTypeLabelColor(
  eventColor: string | null | undefined,
): string {
  const color = eventColor ?? CATALOG_TILE_DEFAULT_EVENT_COLOR;
  return `var(--mantine-color-${color}-${CATALOG_TILE_EVENT_TYPE_LABEL_SHADE})`;
}
